import { safeGetStorageItem } from './browserStorage';

export const CUSTOM_AUTH_TOKEN_STORAGE_KEY = 'customAuthToken';
export const AUTH0_SPA_JS_CACHE_PREFIX = '@@auth0spajs@@';

const hasNonEmptyParam = (params: URLSearchParams, name: string): boolean => {
  const value = params.get(name);
  return Boolean(value && value.length > 0);
};

/**
 * Auth0 hosted login and BotSpot auto-login both return to `/` with query
 * params before application auth state has settled. Treat those as a session
 * hint so the public homepage does not flash during the handoff.
 */
export const hasPendingAuthReturnParams = (search: string): boolean => {
  const params = new URLSearchParams(search);
  if (hasNonEmptyParam(params, 'autoLoginToken')) {
    return true;
  }
  return hasNonEmptyParam(params, 'code') && hasNonEmptyParam(params, 'state');
};

const readLocalStorageKey = (index: number): string | null => {
  try {
    return window.localStorage.key(index);
  } catch {
    return null;
  }
};

const readLocalStorageLength = (): number => {
  try {
    return window.localStorage.length;
  } catch {
    return 0;
  }
};

export const hasPersistedAuthStorageHint = (): boolean => {
  if (safeGetStorageItem('localStorage', CUSTOM_AUTH_TOKEN_STORAGE_KEY)) {
    return true;
  }

  const length = readLocalStorageLength();
  for (let index = 0; index < length; index += 1) {
    const key = readLocalStorageKey(index);
    if (key && key.startsWith(AUTH0_SPA_JS_CACHE_PREFIX)) {
      return true;
    }
  }
  return false;
};

export const hasPersistedAuthSessionHint = (search: string): boolean => {
  return hasPendingAuthReturnParams(search) || hasPersistedAuthStorageHint();
};

export const shouldPrefetchPublicHomepage = (pathname: string): boolean => pathname === '/';

export const prefetchPublicHomepage = <T>(
  pathname: string,
  loadHomepage: () => T | Promise<T>,
): void => {
  if (!shouldPrefetchPublicHomepage(pathname)) {
    return;
  }
  // Prefetch is optional. Rendering remains responsible for surfacing a failed
  // import through the page boundary; avoid an unhandled speculative rejection.
  void Promise.resolve()
    .then(loadHomepage)
    .catch(() => {});
};
